import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { BookOpen, ChevronRight, ArrowLeft, Library as LibraryIcon } from 'lucide-react';
import LanguageFlag from '../components/LanguageFlag';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SeriesList() {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
        setSeries(res.data.series || []);
      } catch (err) {
        console.error('Failed to load series:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="series-list-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/library')} data-testid="series-back-library">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <LibraryIcon className="h-8 w-8 text-primary" />
                Story Series
              </h1>
              <p className="text-muted-foreground mt-1">
                Themed, levelled collections to read from start to finish
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {series.map((s) => (
              <Card
                key={s.series_id}
                className="group hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/series/${s.series_id}`)}
                data-testid={`series-card-${s.series_id}`}
              >
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-4xl flex-shrink-0" aria-hidden>
                        {s.cover_emoji || '📖'}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-lg truncate group-hover:text-primary transition-colors">
                          {s.title}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <LanguageFlag code={s.language_code} size="sm" />
                          <span>{s.language_name || s.language_code}</span>
                          {s.cefr_level && (
                            <Badge variant="outline" className="text-[10px]">{s.cefr_level}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-3">{s.description}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground pt-2 border-t">
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3.5 w-3.5" />
                      {s.episode_count} {s.episode_count === 1 ? 'episode' : 'episodes'}
                    </span>
                    {s.theme && (
                      <span className="truncate">· {s.theme}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
